import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import ComposeMessaging from '../ComposeMessaging';
import Segment from '../../components/Segment';
import Toast from '../../components/Toast';
import Header from '../../components/Header';
import { MessagingOptions, MessagingErrors } from '../ComposeMessaging/ComposeMessaging';
import { sortByName } from './utils';
import './styles.css';

const urls = {
  support: 'intersectsupport@eab.com',
};

const defaultLabelStyle = {
  fontWeight: 'normal',
};
const supportUrl = <a href={`mailto:${urls.support}`}>{urls.support}</a>;

const messages = defineMessages({
  noMajors: {
    id: 'amnext.messages.majors.no',
    defaultMessage: `We were unable to find majors for your institution. Majors are imported via IPEDS or provided to Intersect by your institution. If you have recently added more majors to IPEDS or would like to provide Intersect with majors, please contact support at {supportUrl}.`, // eslint-disable-line
  },
  saveSuccess: {
    id: 'amnext.messages.majors.message.success',
    defaultMessage: 'Major messages have been successfully updated.',
  },
  shouldCheckMessages: {
    id: 'amnext.messages.majors.message.check',
    defaultMessage: 'Please, Your message has been formatted. Please, check them again.',
  },
  unknownError: {
    id: 'amnext.messages.majors.error.unknown',
    defaultMessage: 'Something is wrong in your data',
  },
  mainTitle: {
    id: 'amnext.messages.majors.title',
    defaultMessage: 'Majors',
  },
  firstPar: {
    id: 'amnext.messages.majors.par.first',
    defaultMessage: 'Select a category to compose messages for students interested in majors offered by your institution.',
  },
  note: {
    id: 'amnext.messages.majors.note',
    defaultMessage: 'Note: unselected categories will not display to students.',
  },

  footerMessage: {
    id: 'amnext.messages.majors.footer',
    defaultMessage: 'The majors above were imported from IPEDS or provided to Intersect by your institution. If you need to update your institution\'s majors, please contact support at {supportUrl}.', // eslint-disable-line
  },

  optionNoMessage: {
    id: 'amnext.messages.majors.option.nomessage',
    defaultMessage: 'Display without message',
  },
  optionSameForAll: {
    id: 'amnext.messages.majors.option.sameforall',
    defaultMessage: 'Show same message to all majors',
  },
  optionDifferentiate: {
    id: 'amnext.messages.majors.option.differentiate',
    defaultMessage: 'Differentiate message per major',
  },
});

function formatMesssages(data) {
  const { majors } = data;
  const subCategories = {};

  const categories = majors && majors.map((me) => {
    const { category, categoryId, enabled, messageOption, defaultMessage, messages: messageList } = me;
    subCategories[categoryId] = messageList ?
      messageList.map((subMessage) => {
        const { majorId, major, message: majorMessage } = subMessage;
        return {
          id: majorId,
          name: major,
          message: majorMessage,
        };
      }).sort(sortByName): [];

    const newCategory = {
      disabled: !enabled,
      type: messageOption || MessagingOptions.NOMESSAGE,
      defaultMessage,
      id: categoryId,
      name: category,
      messages: subCategories[categoryId].map((sc) => ({ refId: sc.id, message: sc.message })),
    };

    if (messageOption === MessagingOptions.SAMEFORALL) {
      newCategory.messaging = newCategory.defaultMessage;
    } else if (messageOption === MessagingOptions.DIFFERENTIATE) {
      newCategory.messaging = newCategory.messages;
    }

    return newCategory;
  });

  return {
    categories: categories.sort(sortByName),
    subCategories,
  };
}

class MajorsMessagingPage extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    messages: PropTypes.object,
    majors: PropTypes.array,
    updateMajorsMessaging: PropTypes.func.isRequired,
  };

  static defaultProps = {
    messages: {
      majors: [],
    },
    majors: [],
  };

  static getDerivedStateFromProps(props, state) {
    const { messages: messageList } = props;
    const newState = {};

    if (messageList !== state.prevMessages) {
      const parsed = formatMesssages(messageList);
      newState.categories = parsed.categories;
      newState.subCategories = parsed.subCategories;
      newState.prevMessages = messageList;
    }
    return newState;
  }

  state = {
    showSuccess: null,
    showFailure: null,
    prevMessages: null,
    categories: [],
    subCategories: {},
  };

  handleSuccessToastDismiss = () => {
    this.setState({
      showSuccess: null,
    });
  }

  handleFailureToastDismiss = () => {
    this.setState({
      showFailure: null,
    });
  }

  handleSubmit = (data) => {
    const { intl } = this.props;
    const { subCategories } = this.state;
    const sendable = data.reduce((acc, cat) => {
      const { id, name, disabled, type, defaultMessage, messages: messageList, messaging } = cat;
      const newCat = {
        categoryId: id,
        category: name,
        messageOption: type,
        enabled: !disabled,
      };
      newCat.defaultMessage = defaultMessage || '';
      newCat.messages = messageList;
      if (type === MessagingOptions.SAMEFORALL) {
        newCat.defaultMessage = messaging;
      } else if (type === MessagingOptions.DIFFERENTIATE) {
        newCat.messages = messaging;
      }

      newCat.messages = Array.isArray(newCat.messages) ? newCat.messages.map((me) => {
        const major = subCategories[id.toString()].find((ma) => ma.id === me.refId);

        return {
          majorId: me.refId,
          message: me.message || '',
          major: major && major.name,
        };
      }) : [];

      acc.push(newCat);
      return acc;
    }, []);

    return this.props.updateMajorsMessaging({
      variables: {
        input: {
          version: this.props.messages.version,
          majors: sendable,
        },
      },
    }).then(() => {
      this.setState({
        showSuccess: intl.formatMessage(messages.saveSuccess),
      });
    })
      .catch((error) => {
        this.setState({
          showFailure: error.message,
        });
        return Promise.reject();
      });
  }

  handleError = ({ type }) => {
    const { intl } = this.props;
    if (type === MessagingErrors.CHECK_MESSAGES) {
      this.setState({
        showFailure: intl.formatMessage(messages.shouldCheckMessages),
      });
    } else {
      this.setState({
        showFailure: intl.formatMessage(messages.unknownError),
      });
    }
  }

  renderComposer() {
    const { categories, subCategories } = this.state;
    const { intl } = this.props;
    const majorsOptions = {
      [MessagingOptions.NOMESSAGE]: intl.formatMessage(messages.optionNoMessage),
      [MessagingOptions.SAMEFORALL]: intl.formatMessage(messages.optionSameForAll),
      [MessagingOptions.DIFFERENTIATE]: intl.formatMessage(messages.optionDifferentiate),
    };

    return (
      <React.Fragment>
        <p>{intl.formatMessage(messages.firstPar)}</p>
        <p>{intl.formatMessage(messages.note)}</p>

        <ComposeMessaging
          options={majorsOptions}
          list={categories}
          refList={subCategories}
          allowDisable
          refsPerEntry
          onSubmit={this.handleSubmit}
          onError={this.handleError}
          labelStyle={defaultLabelStyle}
        >
          <p styleName="footer"><FormattedMessage
            {...messages.footerMessage}
            values={{ supportUrl }}
          />
          </p>
        </ComposeMessaging>
      </React.Fragment>
    );
  }

  render() {
    const { showFailure, showSuccess, categories } = this.state;
    const { intl } = this.props;

    return (
      <Segment>
        <Toast
          active={!!showSuccess}
          title=""
          kind="success"
          description={showSuccess}
          onDeactivate={this.handleSuccessToastDismiss}
        />
        <Toast
          active={!!showFailure}
          title=""
          kind="error"
          description={showFailure}
          onDeactivate={this.handleFailureToastDismiss}
        />
        <Header as="h2" size="medium">{intl.formatMessage(messages.mainTitle)}</Header>
        { categories.length ? this.renderComposer() :
          (
            <p>
              <FormattedMessage
                {...messages.noMajors}
                values={{ supportUrl }}
              />
            </p>
          )}

      </Segment>
    );
  }
}

export default injectIntl(MajorsMessagingPage);
